import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api.js';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check authentication status on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const { isLoggedIn: loggedIn, user: userData } = await api.isLoggedIn();
      setIsLoggedIn(loggedIn);
      setUser(userData);
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsLoggedIn(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, eula) => {
    try {
      const result = await api.login(email, password, eula);
      
      // If login returns user data, it means 2FA was skipped (trusted device)
      if (result.user) {
        await checkAuthStatus();
      }
      
      return result;
    } catch (error) {
      throw error;
    }
  };

  const verify2FA = async (code, skip2FA = false) => {
    try {
      const result = await api.verify2FA(code, skip2FA);
      await checkAuthStatus();
      return result;
    } catch (error) {
      throw error;
    }
  };

  const resend2FA = async () => {
    try {
      await api.resend2FA();
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggedIn(false);
      setUser(null);
    }
  };

  const revokeTrustedDevice = async () => {
    try {
      await api.revokeTrustedDevice();
    } catch (error) {
      console.error('Revoke trusted device failed:', error);
      throw error;
    }
  };

  const revokeAllTrustedDevices = async () => {
    try {
      await api.revokeAllTrustedDevices();
    } catch (error) {
      console.error('Revoke all trusted devices failed:', error);
      throw error;
    }
  };

  const changePassword = async (currentPassword, newPassword, confirmPassword) => {
    try {
      await api.changePassword(currentPassword, newPassword, confirmPassword);
      return true;
    } catch (error) {
      throw error;
    }
  };

  const register = async (email, password, confirmPassword, eula) => {
    try {
      await api.register(email, password, confirmPassword, eula);
      return true;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    isLoggedIn,
    loading,
    login,
    verify2FA,
    resend2FA,
    logout,
    register,
    checkAuthStatus,
    revokeTrustedDevice,
    revokeAllTrustedDevices,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 